import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchBlogs();
  }, [filter]);

  const fetchBlogs = async () => {
    try {
      const url = filter === 'all' ? `${API}/blogs` : `${API}/blogs?category=${filter}`;
      const response = await axios.get(url);
      setBlogs(response.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All Posts' },
    { value: 'molecular-hydrogen', label: 'Molecular Hydrogen' },
    { value: 'skincare', label: 'Skincare' },
    { value: 'infrared-therapy', label: 'Infrared Therapy' },
    { value: 'general', label: 'General Wellness' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-[#0D2847] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Wellness Blog</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Discover insights, research, and tips on molecular hydrogen, skincare, and infrared therapy.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <Button
                key={cat.value}
                variant={filter === cat.value ? 'default' : 'outline'}
                onClick={() => setFilter(cat.value)}
                className={filter === cat.value ? 'bg-[#0D2847]' : ''}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center">
              <p className="text-gray-600">Loading posts...</p>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center">
              <p className="text-gray-600">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <Card key={blog.id} className="hover:shadow-lg transition-shadow">
                  {blog.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img src={blog.image_url} alt={blog.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-xl hover:text-[#0D2847] transition-colors">
                      <Link to={`/blog/${blog.id}`}>{blog.title}</Link>
                    </CardTitle>
                    <CardDescription>{blog.excerpt}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <User size={14} className="mr-1" />
                      <span className="mr-4">{blog.author}</span>
                      <Calendar size={14} className="mr-1" />
                      <span>{new Date(blog.date).toLocaleDateString()}</span>
                    </div>
                    <Link to={`/blog/${blog.id}`}>
                      <Button variant="outline" className="w-full">
                        Read More <ArrowRight size={16} className="ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
